<template>
  <header ref="menuRef"
    class="sticky top-0 z-40 border-b border-border bg-[color-mix(in_srgb,var(--background)_88%,transparent)] backdrop-blur-md">
    <div class="mx-auto flex h-[var(--header-h)] max-w-[var(--container)] items-center gap-4 px-6">
      <Commerce-Logo />

      <!-- Desktop nav -->
      <nav class="hidden items-center gap-1 lg:flex" aria-label="منوی اصلی">
        <ul class="flex items-center gap-1">
          <li v-for="item in navItems" :key="item.label">
            <!-- Simple link -->
            <NuxtLink v-if="item.type === 'link' && isInternalLink(item.to)" :to="item.to"
              class="rounded-lg px-3 py-2 text-sm font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-secondary">
              {{ item.label }}
            </NuxtLink>

            <a v-else-if="item.type === 'link'" :href="item.to"
              class="rounded-lg px-3 py-2 text-sm font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-secondary">
              {{ item.label }}
            </a>

            <!-- Dropdown -->
            <div v-else
              class="rounded-lg hover:bg-secondary transition-colors relative group cursor-pointer hidden lg:flex items-center font-normal text-sm px-3">
              <span
                class="flex items-center gap-2 py-2 text-sm font-medium text-muted-foreground hover:text-foreground">
                {{ item.label }}
              </span>

              <img src="/svg/arrow-down.svg" class="group-hover:rotate-180" alt="" aria-hidden="true" />
              <span v-if="item.label === 'خدمات پرداخت آنلاین'"
                class="-ms-1 flex items-center gap-0.5 rounded-full bg-gradient-to-r from-[#315CFF] via-[#4B8DFF] to-[#6A5CFF] px-2 py-0.5 text-[10px] font-bold text-white shadow-[0_0_10px_rgba(76,108,255,0.35)]">
                <!-- <span class="pt-0.5">✦</span> -->
                <span class="">جدید</span>
              </span>

              <div class="absolute top-full pt-2 hidden group-hover:block w-full">
                <div class="text-[#2626BF] bg-white rounded-xl shadow-lg px-4 p-3 text-sm z-50 w-max">
                  <template v-for="subItem in item.items" :key="subItem.label">
                    <NuxtLink v-if="isInternalLink(subItem.to)" :to="subItem.to"
                      class="flex items-center gap-1 mb-4 last:mb-0">
                      {{ subItem.label }}
                    </NuxtLink>

                    <a v-else :href="subItem.to" class="flex items-center gap-1 mb-4 last:mb-0">
                      {{ subItem.label }}
                    </a>
                  </template>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </nav>

      <div class="mr-auto hidden items-center gap-2 lg:flex">
        <template v-if="isLoggedIn">
          <a :href="siteUrl('/profile/dashboard')">
            <Commerce-Button variant="outline" size="sm">
              داشبورد
            </Commerce-Button>
          </a>

          <div class="relative" @mouseenter="isOpen = true" @mouseleave="isOpen = false">
            <Commerce-Button size="sm" variant="outline" :label="'حساب کاربری'" :active="isOpen" class="w-20!">
              <div class="flex gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"
                  aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <circle cx="12" cy="9" r="3" />
                  <path d="M6.8 19c.9-2.4 2.7-3.8 5.2-3.8s4.3 1.4 5.2 3.8" />
                </svg>

                <img src="/svg/chevron-down.svg" width="16" height="16" alt="" :class="[
                  'transition-transform duration-300',
                  isOpen ? 'rotate-180' : 'rotate-0',
                ]" />
              </div>
            </Commerce-Button>

            <Transition enter-active-class="transition ease-out duration-200" enter-from-class="opacity-0 translate-y-3"
              enter-to-class="opacity-100 translate-y-0" leave-active-class="transition ease-in duration-150"
              leave-from-class="opacity-100 translate-y-0" leave-to-class="opacity-0 translate-y-3">
              <div v-if="isOpen" class="absolute left-[-5px] top-full w-[121px] pt-2">
                <div
                  class="flex w-full flex-col items-start gap-4 rounded-lg border border-border bg-[#f7f8fd] p-4 text-base font-medium shadow-md">
                  <a :href="siteUrl('/profile/')" class="text-foreground">
                    پروفایل
                  </a>

                  <button class="cursor-pointer text-foreground" @click="onLogoutClick">
                    خروج
                  </button>
                </div>
              </div>
            </Transition>
          </div>
        </template>

        <template v-else>
          <Commerce-Button variant="ghost" size="sm" @click="navigateTo('/auth?mode=login')">
            ورود
          </Commerce-Button>

          <Commerce-Button size="sm" variant="primary" @click="navigateTo('/auth?mode=signup')">
            ثبت‌نام
          </Commerce-Button>
        </template>
      </div>

      <!-- Mobile toggle -->
      <div class="mr-auto lg:hidden">
        <Commerce-IconButton :label="'منو'" @click="open = !open" :active="open">
          ☰
        </Commerce-IconButton>
      </div>
    </div>

    <!-- Mobile drawer -->
    <div v-if="open"
      class="absolute left-0 right-0 top-full border-t border-border bg-card px-6 py-4 lg:hidden pj-in shadow-xs">
      <nav class="flex flex-col gap-1" aria-label="منوی اصلی">
        <ul class="flex flex-col gap-1">
          <li v-for="item in navItems" :key="item.label" class="py-2.5">
            <!-- Mobile simple link -->
            <NuxtLink v-if="item.type === 'link' && isInternalLink(item.to)" :to="item.to"
              class="rounded-lg px-3 text-right text-sm font-medium text-foreground hover:bg-secondary"
              @click="open = false">
              {{ item.label }}
            </NuxtLink>

            <a v-else-if="item.type === 'link'" :href="item.to"
              class="rounded-lg px-3 text-right text-sm font-medium text-foreground hover:bg-secondary"
              @click="open = false">
              {{ item.label }}
            </a>

            <!-- Mobile dropdown -->
            <template v-else>
              <div
                class="flex items-center justify-start gap-1 rounded-lg px-3 text-right text-sm font-medium text-foreground">
                {{ item.label }}
                <span v-if="item.label === 'خدمات پرداخت آنلاین'"
                  class="flex items-center gap-0.5 rounded-full bg-gradient-to-r from-[#315CFF] via-[#4B8DFF] to-[#6A5CFF] px-2 py-0.5 text-[10px] font-bold text-white shadow-[0_0_10px_rgba(76,108,255,0.35)]">
                  <!-- <span class="pt-0.5">✦</span> -->
                  <span class="">جدید</span>
                </span>
              </div>

              <ul class="mt-1 flex flex-col gap-1 pr-3">
                <li v-for="subItem in item.items" :key="subItem.label">
                  <NuxtLink v-if="isInternalLink(subItem.to)" :to="subItem.to"
                    class="block rounded-lg px-3 py-2 text-right text-sm text-muted-foreground hover:bg-secondary"
                    @click="open = false">
                    {{ subItem.label }}
                  </NuxtLink>

                  <a v-else :href="subItem.to"
                    class="block rounded-lg px-3 py-2 text-right text-sm text-muted-foreground hover:bg-secondary"
                    @click="open = false">
                    {{ subItem.label }}
                  </a>
                </li>
              </ul>
            </template>
          </li>
        </ul>
      </nav>

      <div class="mt-3 flex gap-2 border-t border-border pt-3">
        <template v-if="isLoggedIn">
          <a :href="siteUrl('/profile/dashboard')" class="flex-1">
            <Commerce-Button full variant="outline">
              داشبورد
            </Commerce-Button>
          </a>

          <a :href="siteUrl('/profile/')" class="flex-1">
            <Commerce-Button full variant="outline">
              پروفایل
            </Commerce-Button>
          </a>

          <Commerce-Button full variant="outline" :disabled="loadingLogout" @click="onLogoutClick">
            {{ loadingLogout ? 'در حال خروج...' : 'خروج' }}
          </Commerce-Button>
        </template>

        <template v-else>
          <Commerce-Button full variant="outline" @click="navigateTo('/auth?mode=login'); open = false" size="md">
            ورود
          </Commerce-Button>

          <Commerce-Button full @click="navigateTo('/auth?mode=signup'); open = false" size="md" variant="primary">
            ثبت‌نام
          </Commerce-Button>
        </template>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
const siteUrl = useSiteUrl()

import { onClickOutside } from '@vueuse/core'
import { computed, onMounted, ref } from 'vue'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import { logoutAction } from '~/api/functions/auth/logout.action'

type NavLink = {
  label: string
  to: string
}

type NavDropdown = {
  label: string
  type: 'dropdown'
  items: NavLink[]
}

type NavItem = (NavLink & { type: 'link' }) | NavDropdown

const { auth, initAuth, clearAuth } = useAuth()
const { toast } = useToast()
const router = useRouter()

const isOpen = ref(false)
const open = ref(false)
const menuRef = ref()

const isLoggedIn = computed(() => !!auth.value)

const navItems: NavItem[] = [
  {
    type: 'dropdown',
    label: 'خدمات پرداخت آنلاین',
    items: [
      {
        label: 'خدمات پرداخت بین‌المللی',
        to: '/',
      },
      {
        label: 'گیفت‌کارت',
        to: '/gift-cards',
      },
      {
        label: 'سفارش اختصاصی',
        to: '/custom',
      },
    ],
  },
  {
    type: 'link',
    label: 'خدمات انتقال آنلاین',
    to: siteUrl('/profile'),
  },
  {
    type: 'dropdown',
    label: 'پشتیبانی',
    items: [
      {
        label: 'تلگرام',
        to: 'https://t.me/+989360950650',
      },
      {
        label: 'واتساپ',
        to: 'https://wa.me/989360950650',
      },
    ],
  },
  {
    type: 'dropdown',
    label: 'راهنما',
    items: [
      {
        label: 'قوانین پی‌جیب',
        to: siteUrl('/terms'),
      },
      {
        label: 'سوالات متداول',
        to: siteUrl('/#questions'),
      },
      {
        label: 'حذف حساب کاربری',
        to: siteUrl('/account-deletion'),
      },
    ],
  },
  {
    type: 'link',
    label: 'وبلاگ',
    to: siteUrl('/blog/'),
  },
  {
    type: 'link',
    label: 'درباره ما',
    to: siteUrl('/about/'),
  },
]

function isInternalLink(to: string) {
  return to.startsWith('/')
}

function navigateTo(path: string) {
  router.push(path)
  window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
}

onClickOutside(menuRef, () => {
  open.value = false
})

const loadingLogout = ref(false)

async function onLogoutClick() {
  try {
    loadingLogout.value = true
    isOpen.value = false
    open.value = false

    await logoutAction()

    clearAuth()

    toast.success('با موفقیت خارج شدید')

    router.push('/')
  } catch (error) {
    toast.error(getErrorMessage(error))
  } finally {
    loadingLogout.value = false
  }
}

onMounted(() => {
  initAuth()
})
</script>
